/**
 * Shared confirmation dialog. Replaces browser confirm() with a styled modal.
 * Use via the useConfirm() hook for ergonomic usage.
 */

import { useState, useCallback, useRef } from 'react'
import { AlertTriangle } from 'lucide-react'
import Button from './Button'

interface ConfirmDialogProps {
  open: boolean
  onConfirm: () => void
  onCancel: () => void
  title?: string
  message: string
  confirmLabel?: string
  confirmVariant?: 'primary' | 'danger'
  loading?: boolean
}

export default function ConfirmDialog({
  open,
  onConfirm,
  onCancel,
  title = 'Confirm',
  message,
  confirmLabel = 'Confirm',
  confirmVariant = 'danger',
  loading = false,
}: ConfirmDialogProps) {
  const overlayRef = useRef<HTMLDivElement>(null)

  if (!open) return null

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:px-4"
      onClick={(e) => { if (e.target === overlayRef.current) onCancel() }}
    >
      <div className="fixed inset-0 bg-black/40" />
      <div className="relative bg-white shadow-xl w-full sm:max-w-sm rounded-t-2xl sm:rounded-xl p-5 sm:p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
            <AlertTriangle size={20} className="text-red-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-600 mt-1">{message}</p>
          </div>
        </div>
        <div className="flex gap-3 justify-end">
          <Button variant="secondary" size="md" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button variant={confirmVariant} size="md" onClick={onConfirm} loading={loading}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  )
}

/** Hook for ergonomic confirm dialog usage. Returns [ConfirmDialogNode, confirm()] */
export function useConfirm() {
  const [state, setState] = useState<{
    open: boolean
    message: string
    title?: string
    confirmLabel?: string
    confirmVariant?: 'primary' | 'danger'
    resolve?: (v: boolean) => void
  }>({ open: false, message: '' })

  const confirm = useCallback(
    (opts: { message: string; title?: string; confirmLabel?: string; confirmVariant?: 'primary' | 'danger' }) =>
      new Promise<boolean>((resolve) => {
        setState({ open: true, ...opts, resolve })
      }),
    [],
  )

  const handleConfirm = useCallback(() => {
    state.resolve?.(true)
    setState((s) => ({ ...s, open: false }))
  }, [state.resolve])

  const handleCancel = useCallback(() => {
    state.resolve?.(false)
    setState((s) => ({ ...s, open: false }))
  }, [state.resolve])

  const node = (
    <ConfirmDialog
      open={state.open}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
      title={state.title}
      message={state.message}
      confirmLabel={state.confirmLabel}
      confirmVariant={state.confirmVariant}
    />
  )

  return [node, confirm] as const
}
